import type { TFunction } from 'i18next';
import type { Dispatch, DragEvent, SetStateAction } from 'react';
import type { DataGridColumnProps } from '../components/DataGrid/DataGrid';
import type { DataGridColumnOptionsProps } from '../components/DataGridControl/DataGridControl';
import type { DropdownOption } from '../components/Dropdown/DropdownOption/DropdownOption';
import type { UserLocalStorage } from '../types/localStorage';
import type {
  ContentObject,
  ContentTypeDefinition,
  Media,
  MediaHydrated,
  Variant,
} from './flotiq-client/types/api-response';

import TagManager from 'react-gtm-module';

import {
  GithubWhiteIcon,
  GoogleWhiteIcon,
  WindowsWhiteLogo,
} from '../images/shapes';

import i18n from '../i18n';
import { getEnv } from './env';

const apiUrl = getEnv('VITE_FLOTIQ_API_URL');

export const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

export const getTimestamp = (date?: string | null) => {
  if (!date) return null;

  const timestamp = Date.parse(date);
  return Number.isNaN(timestamp) ? null : timestamp;
};

/**
 * Authentication buttons array
 */
export const authButtonsData = [
  {
    name: 'Microsoft',
    logo: <WindowsWhiteLogo className="h-full w-full" />,
    url: `${apiUrl}/connect/azure`,
  },
  {
    name: 'GitHub',
    logo: <GithubWhiteIcon className="h-full w-full dark:text-black" />,
    url: `${apiUrl}/connect/github`,
  },
  {
    name: 'Google',
    logo: <GoogleWhiteIcon className="h-full w-full" />,
    url: `${apiUrl}/connect/google`,
  },
];

/**
 * Get login error text from status and activated search params
 */
export const getLoginErrorText = (
  status: string | null,
  t: TFunction,
  activated?: string | null,
) => {
  if (activated === 'false') return t('Activation.False');
  else if (status === 'bad_verification_code')
    return t('Login.VerificationCode');
  else if (status === 'user_disabled') return t('Login.UserDisabled');
  else if (status === 'azure_email_not_available')
    return t('Login.AzureNotAvailable');
  else if (status && status !== 'success') return t('Login.DefaultError');
  return '';
};

/**
 * Generate object with test id props if testId is defined
 */
export const getTestProps = (
  testId?: string,
  suffix?: string,
  testIdPropName: string = 'data-testid',
) => {
  if (
    import.meta.env.MODE !== 'test' &&
    localStorage['flotiq.enable-test-ids'] !== 'true'
  ) {
    return {};
  }
  if (!testId) return {};
  if (!suffix) {
    return { [testIdPropName]: testId };
  }
  return {
    [testIdPropName]: `${testId}-${suffix}`,
  };
};

/**
 * Read key value deep inside object
 */
export const deepReadKeyValue = (key: string, object: unknown): unknown => {
  return key
    .split(/[[.\]]/)
    .filter((kp) => !!kp)
    .reduce((nestedOptions, keyPart) => {
      if (nestedOptions == null) return undefined;
      if (Array.isArray(nestedOptions) && /^\d+$/.test(keyPart)) {
        return nestedOptions[+keyPart];
      }
      if (typeof nestedOptions === 'object' && keyPart in nestedOptions) {
        return (nestedOptions as Record<string, unknown>)[keyPart];
      }
      return undefined;
    }, object);
};

/**
 * Replace every {key} token in a template with the deep value read from object.
 */
export const interpolateTemplate = (
  template: string,
  object: Record<string, unknown>,
): string => {
  return template.replace(/{(?<key>[^{}]+)}/g, (...params) => {
    const { key } = params[params.length - 1] as { key: string };
    const value = deepReadKeyValue(key, object);
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return '';
  });
};

/**
 * Insert value into object under path referred by key
 */
export const deepAssignKeyValue = (
  key: string,
  value: any,
  options: Record<string, any>,
) =>
  key
    .split(/[[.\]]/)
    .filter((kp) => !!kp)
    .reduce((nestedOptions, keyPart, index, keysArray) => {
      if (!nestedOptions[keyPart]) {
        const isArray =
          index < keysArray.length - 1 && /^\d+$/.test(keysArray[index + 1]);

        if (!isArray) {
          nestedOptions[keyPart] = {};
        } else {
          nestedOptions[keyPart] = [];
        }
      }
      if (index >= keysArray.length - 1) {
        if (value === undefined) delete nestedOptions[keyPart];
        else nestedOptions[keyPart] = value;
      }
      return nestedOptions[keyPart];
    }, options);

/**
 * Get tag url from tag id
 */
export const getTagUrl = (tagId: string) => {
  if (!tagId) {
    return '';
  }
  return `/api/v1/content/_tag/${tagId}`;
};

/**
 * Convert size in bytes into displayable format
 */
export const getDataSize = (
  bytes: number,
  precision: number = 1,
  unitsPattern: Array<any> = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'],
) => {
  if (isNaN(bytes) || !isFinite(bytes)) return '-';

  const number = bytes < 1 ? 0 : Math.floor(Math.log(bytes) / Math.log(1000));

  return (
    Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(bytes / Math.pow(1000, number)) +
    ' ' +
    unitsPattern[number]
  );
};

/**
 * Generate url to API docs for given user
 */
export const generateApiDocs = (
  userDetails?: { organization?: string },
  apiKeyObject?: { apiKey?: string },
) => {
  if (!getEnv('VITE_API_DOC_GENERATOR_URL')) return '';
  const organizationId = userDetails?.organization;
  const token = window.btoa(`${apiKeyObject?.apiKey}@${organizationId}`);
  const api = `${
    getEnv('VITE_FLOTIQ_INTERNAL_API_URL') || getEnv('VITE_FLOTIQ_API_URL')
  }/api`;
  const params = new URLSearchParams({
    token,
    api,
  });
  return `${getEnv('VITE_API_DOC_GENERATOR_URL')}?${params.toString()}`;
};

/**
 * Format error message to string
 */
export const formatErrorToString = (error?: unknown): string => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (Array.isArray(error))
    return error
      .filter((element) => !!element)
      .map((element) => formatErrorToString(element))
      .join(', ');
  return Object.keys(error)
    .filter((key) => error[key as keyof typeof error])
    .map((key) => {
      const messageKey = key ? `${key}: ` : '';
      return `${messageKey}${formatErrorToString(error[key as keyof typeof error])}`;
    })
    .join(', ');
};

/**
 * Generate checksum for given strings
 */
export const strToChecksum = (...args: string[]) => {
  let hash = 0,
    i,
    chr,
    len;
  const text = args.join('-');
  if (text.length === 0) return hash;
  for (i = 0, len = text.length; i < len; i++) {
    chr = text.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

/**
 * Get initial columns options based on table width
 */
export const getInitialColumnsOptions = (
  availableWidth: number = 0,
  columns: DataGridColumnProps[] = [],
  minWidth: number = 50,
  contentType?: ContentTypeDefinition,
) => {
  const widthMinColumn = minWidth;
  let widthColumn = 50;
  const options = [];
  const cols = [];

  /**
   * First pass - determine columns visibility
   */
  if (columns?.length > 0) {
    for (const columnItemKey in columns) {
      /**
       * If field is a normal field:
       *  - return value of `hidden` attribute
       *  - if not `hidden` attribute is defined - mark as visible
       * if field is one of internal fields:
       *  - mark hidden by default
       *  - if showByDefault property is set - use its value
       */
      let isHidden;
      if (
        typeof contentType?.metaDefinition?.propertiesConfig[
          columns[columnItemKey].accessor
        ] !== 'undefined'
      ) {
        isHidden =
          typeof contentType?.metaDefinition?.propertiesConfig[
            columns[columnItemKey].accessor
          ]?.hidden !== 'undefined'
            ? contentType.metaDefinition?.propertiesConfig[
                columns[columnItemKey].accessor
              ].hidden
            : false;
      } else if (columns[columnItemKey].accessor.startsWith('internal.')) {
        isHidden =
          typeof columns[columnItemKey].showByDefault !== 'undefined'
            ? !columns[columnItemKey].showByDefault
            : true;
      } else
        isHidden = !!(contentType && columns[columnItemKey].accessor === 'id');
      columns[columnItemKey].hide = isHidden;
    }
  }

  /**
   * Second pass - determine visible columns widths
   */

  let columnsCounter = columns.length;

  let widthToSubtract = 0;

  if (columns.length > 0) {
    columns.forEach((column) => {
      if (column.hide) {
        columnsCounter -= 1;
      } else if (column.baseWidth) {
        widthToSubtract += column.baseWidth;
        columnsCounter -= 1;
      }
    });
  }

  const tableSpareWidth = availableWidth - widthToSubtract;

  if (tableSpareWidth > 0 && columnsCounter) {
    const calculateColumnWidth = Number(
      (tableSpareWidth / columnsCounter).toFixed(0),
    );
    widthColumn =
      calculateColumnWidth >= widthMinColumn
        ? calculateColumnWidth
        : widthMinColumn;
  }

  if (columns.length > 0) {
    for (const columnItemKey in columns) {
      const columnWidth = columns[columnItemKey].baseWidth || widthColumn;

      cols.push({
        ...columns[columnItemKey],
        width: columnWidth,
      });

      options.push({
        width: columnWidth,
        colId: columns[columnItemKey].accessor,
        hide: columns[columnItemKey].hide,
      });
    }
  }

  return { options, cols };
};

/**
 * Set initial grid options
 */
export const setInitialGridOptions = (
  cols: DataGridColumnProps[],
  minWidth: number,
  availableWidth: number,
  setGridOptions: Dispatch<
    SetStateAction<DataGridColumnOptionsProps[] | undefined>
  >,
  setColumns: Dispatch<SetStateAction<DataGridColumnProps[]>>,
  contentType?: ContentTypeDefinition,
) => {
  const initGridOptions = getInitialColumnsOptions(
    availableWidth,
    cols,
    minWidth,
    contentType,
  );
  setGridOptions(initGridOptions.options);
  setColumns?.(initGridOptions.cols);
};

/**
 * Set initial grid options
 */
export const updateLocalStorageGridOptions = (
  order: string[],
  savedGridOptions: DataGridColumnOptionsProps[] | undefined,
  saveGridOptions: Dispatch<
    SetStateAction<DataGridColumnOptionsProps[] | undefined>
  >,
) => {
  const newOptions = [];
  const validOptions = [];
  let shouldUpdate = false;
  const currentLocalGridOption = savedGridOptions || [];

  // Add new grid options
  for (const item in order) {
    const indexInLocal = currentLocalGridOption.findIndex(
      (el) => el.colId === order[item],
    );
    if (indexInLocal < 0) {
      newOptions.push({
        width: 100,
        colId: order[item],
        hide: false,
      });
      shouldUpdate = true;
    }
  }

  // Validate saved local storage based on provided keys from order
  for (const option in currentLocalGridOption) {
    const optionID = currentLocalGridOption[option].colId;
    if (optionID === 'id' || optionID === 'action') {
      validOptions.push(currentLocalGridOption[option]);
    } else {
      const indexInOrder = order.findIndex((el) => el === optionID);

      if (indexInOrder > -1) {
        validOptions.push(currentLocalGridOption[option]);
      } else {
        // Options was in saved but is no longer in order
        // Will be removed and need to be updated base on new validOptions
        shouldUpdate = true;
      }
    }
  }

  if (shouldUpdate) {
    const updateOptions = [...validOptions, ...newOptions];
    saveGridOptions(updateOptions);
  }
};

/**
 * Function to resize columns
 */
export const resizeColumn = (
  columnWidth?: number,
  dataKey?: string,
  gridOptions: DataGridColumnOptionsProps[] = [],
  setGridOptions?: (options: DataGridColumnOptionsProps[]) => void,
) => {
  // Case: find column index
  const columnIndex = gridOptions?.findIndex((object) => {
    return object['colId'] === dataKey;
  });

  if (columnIndex > -1) {
    const newOptions = [...gridOptions];
    newOptions[columnIndex].width = columnWidth;

    if (setGridOptions) {
      setGridOptions(newOptions);
    } else {
      return newOptions;
    }
  }
};

/**
 * Function to handle multiple error messages on delete with relations issue columns
 */
export const validateErrorMessage = (message: string) => {
  // Case: handle multiple errors
  if (message.split(',').length > 1) {
    const currentErrors = message.split(',');
    const currentMessages = [];
    const messagesUsedInAnother = [];

    // Error Case: another content object
    for (const errorItem of currentErrors) {
      if (errorItem.indexOf('is used in another content object') > -1) {
        messagesUsedInAnother.push(
          errorItem
            .replace('Content object:', '')
            .replace('is used in another content object.', ''),
        );
      } else {
        currentMessages.push(errorItem);
      }
    }
    if (messagesUsedInAnother.length) {
      currentMessages.push(
        'Content objects are used in another content objects:' +
          messagesUsedInAnother.join(','),
      );
    }
    message = currentMessages.join(',');
  }
  return message;
};

/**
 * Function to get initial value for type.
 * Due to form touched state for arrays it has to be the function.
 * Form is setting touched state based on initial values,
 * so it has to be different reference for arrays.
 */
export const getDefaultValueForType = (type: string) => {
  const typeDefaults = {
    string: '',
    number: 0,
    boolean: false,
    array: [],
    object: {},
  };
  return type in typeDefaults
    ? typeDefaults[type as keyof typeof typeDefaults]
    : undefined;
};

/**
 * Calculate content object title based on object itself and definition field order
 */
export const getObjectTitle = (
  object?: ContentObject,
  contentType?: ContentTypeDefinition,
) => {
  if (!object) return '';

  if (object.internal?.objectTitle) return object.internal.objectTitle;

  if (contentType?.metaDefinition?.order) {
    const def = contentType.metaDefinition;
    const firstTextField = def.order.find(
      (field) => def.propertiesConfig[field].inputType === 'text',
    );

    if (firstTextField && object[firstTextField]) return object[firstTextField];
  }

  const assumedTitleFields = ['title', 'name', 'fileName'];

  for (const field of assumedTitleFields) {
    if (typeof object?.[field] === 'string') return object?.[field];
  }

  return object?.id;
};

/**
 * Generate full redirect url, based on PUBLIC_URL env variable
 */
export const generateRedirectUrl = (path = '', encodeUrl = false) => {
  const publicUrl = getEnv('PUBLIC_URL') || window.location.origin;
  const redirectUri = `${publicUrl.replace(/\/{0,10}$/, '')}/${path.replace(
    /^\/{0,10}/,
    '',
  )}`;

  if (!encodeUrl) return redirectUri;

  return encodeURIComponent(redirectUri);
};

/**
 * Truncate text with length
 */
export const getTruncateOnLengthText = (
  value: string | number,
  truncate: boolean,
  truncateLength: number,
) => {
  const currentValue = `${value}`;

  if (
    truncate &&
    truncateLength &&
    currentValue &&
    currentValue.length > truncateLength
  ) {
    return `${currentValue.slice(0, truncateLength)} ...`;
  } else {
    return value;
  }
};

/**
 * Enable the sync webhook type from the environment.
 */
export const isSyncWebhookTypeEnabled = () => {
  const moduleEnabledEnv = getEnv('VITE_MODULE_WEBHOOKS_TYPE');
  if (moduleEnabledEnv) {
    try {
      return JSON.parse(moduleEnabledEnv) || false;
    } catch {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * Sorted by key
 */
export const getSortedBy = <T extends Record<string, any>>(
  data?: T[],
  key?: keyof T,
  direction = 'desc',
) => {
  if (!data) return [];
  if (!key) return data;

  return [...(data || [])]?.sort((a, b) => {
    const keyA = typeof a[key] === 'string' ? a[key].toLowerCase() : a[key];
    const keyB = typeof b[key] === 'string' ? b[key].toLowerCase() : b[key];

    if (keyA > keyB) {
      return direction === 'desc' ? -1 : 1;
    }
    if (keyA < keyB) {
      return direction === 'desc' ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Get sorted folders structure
 */
export const getFoldersStructure = (
  data: ContentTypeDefinition[],
  currentFolderName?: string | null,
  allowFolders?: boolean,
) => {
  const currentData = [];
  const foldersData = [];
  const folderID: Record<
    string,
    {
      id: string;
      name: string;
      label: string;
      folder: boolean;
      children: ContentTypeDefinition[];
    }
  > = {};

  for (const dataKey in data) {
    const itemLabel = data[dataKey].label;

    if (itemLabel?.indexOf('/') > -1) {
      if (allowFolders) {
        const folderName = itemLabel.slice(0, itemLabel.indexOf('/'));

        if (!folderID[folderName]) {
          folderID[folderName] = {
            id: `${folderName}-folder`,
            name: folderName,
            label: folderName,
            folder: true,
            children: [data[dataKey]],
          };
        } else {
          folderID[folderName].children.push(data[dataKey]);
        }
      } else {
        foldersData.push(data[dataKey]);
      }
    } else {
      currentData.push(data[dataKey]);
    }
  }

  if (allowFolders) {
    for (const [, value] of Object.entries(folderID)) {
      foldersData.push(value);
    }
  }

  if (currentFolderName && allowFolders) {
    return getSortedBy(folderID[currentFolderName]?.children, 'label', 'asc');
  } else {
    return [
      ...(getSortedBy(foldersData, 'label', 'asc') || []),
      ...(getSortedBy(currentData, 'label', 'asc') || []),
    ];
  }
};

/**
 * Get formated value size based on mode or value.
 */
export const getFormatedSize = (
  value?: null | string | number | boolean,
  unlimitedText: string = 'unlimited',
  mode: string = '',
) => {
  if (value == null) return null;

  // Case: unlimited
  if (value === -1) {
    return unlimitedText;
  }

  if (typeof value !== 'number') return value;

  if (mode === 'file-size') {
    if (value === 0) return 0;
    return getDataSize(value, 2, ['MB', 'GB', 'TB', 'PB']);
  }

  // Case: unformatted
  return Intl.NumberFormat(i18n.language || 'en').format(value);
};

/**
 * Calculate unique hash from string
 */
export const getHash = (inputString?: string) => {
  if (!inputString || inputString === '') return '0';
  let hash = 0;
  for (let i = 0, len = inputString.length; i < len; i++) {
    const chr = inputString.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash.toString();
};

/**
 * Based on browser need to check images to prevent show broken images e.g. tiff
 */
export const isImagePreviewSupported = (extension: string) => {
  if (!extension) return false;

  const problematicExtensions = ['tif', 'tiff'];
  const agent = navigator?.userAgent?.toLowerCase();
  const otherBrowser = /chrome|android|criOS|fxios|edgios/i.test(agent);
  const isSafari = /safari/i.test(agent);

  // Case: tif,tiff visible on Safari browser
  if (problematicExtensions.includes(extension)) {
    return !otherBrowser && isSafari;
  }

  return true;
};

/**
 * Parse scoped keys docs limit
 */
export const parseScopedKeysDocs = (limit: boolean | string) => {
  if (typeof limit === 'boolean') {
    return limit;
  }

  return Boolean(parseInt(limit));
};

/**
 * Get organization name with only visible first 5 letter and every odd letter
 * @example
 * getMaskedOrganizationName('someOrganization@gmail.com')
 * // returns 'someO*g*n*z*t*o*@*m*i*.*o*'
 */
export const getMaskedOrganizationName = (name?: string) => {
  if (!name) return '';
  return name
    .split('')
    .map((char, index) => {
      if (index < 5 || index % 2 === 0) return char;
      return '*';
    })
    .join('');
};

/**
 * Get crop attributes dictionary from media transforms
 */
export const getCropAttributes = (
  variant: Variant | undefined,
  media: Media | MediaHydrated,
  isInitialCrop: boolean = false,
) => {
  const cropAttributes = variant?.trim;

  const noCropValues =
    isInitialCrop && media
      ? {
          left: media.width / 4,
          top: media.height / 4,
          width: media.width / 2,
          height: media.height / 2,
        }
      : { left: 0, top: 0, width: media.width, height: media.height };

  return cropAttributes || noCropValues;
};

/**
 * Get default properties for image tags
 */
export const getDefaultImageProperties = (): {
  onDragStart: (event: DragEvent<HTMLImageElement>) => void;
} => {
  return {
    onDragStart: (event: DragEvent<HTMLImageElement>) => {
      event.stopPropagation();
    },
  };
};

export const prepareColumns = (
  gridOptions: DataGridColumnOptionsProps[] | undefined,
  t: TFunction,
  testId: string,
  setGridOptions: Dispatch<
    SetStateAction<DataGridColumnOptionsProps[] | undefined>
  >,
  handleInitialGridOptions: (
    cols: DataGridColumnProps[],
    width: number,
  ) => void,
  setColumns: (definedColumns: DataGridColumnProps[]) => void,
  getDefinedColumns: (
    gridOptionsByID: Record<string, DataGridColumnOptionsProps> | undefined,
    hiddenColumnsById: Record<string, boolean | undefined>,
    t: TFunction,
    testId?: string,
    isAllUsers?: boolean,
    filterDatasourceFetch?: (
      accessor: string,
      query?: string,
    ) => Promise<DropdownOption[]>,
    isRoleAdmin?: boolean,
  ) => Record<string, DataGridColumnProps>,
  isAllUsers?: boolean,
  filterDatasourceFetch?: (
    accessor: string,
    query?: string,
  ) => Promise<DropdownOption[]>,
  isRoleAdmin = false,
) => {
  const cols: DataGridColumnProps[] = [];

  const gridOptionsByID = gridOptions?.reduce(
    (acc, elem) => {
      acc[elem.colId] = elem;
      return acc;
    },
    {} as Record<string, DataGridColumnOptionsProps>,
  );

  const hiddenColumnsById: Record<string, boolean | undefined> = {};
  const orderFromLocalStorage = [];

  for (const key in gridOptions) {
    hiddenColumnsById[gridOptions[+key].colId] = gridOptions[+key].hide;
    orderFromLocalStorage.push(gridOptions[+key].colId);
  }

  const definedColumns = getDefinedColumns(
    gridOptionsByID,
    hiddenColumnsById,
    t,
    testId,
    isAllUsers,
    filterDatasourceFetch,
    isRoleAdmin,
  );

  let order = Object.keys(definedColumns);
  order = [...new Set([...(orderFromLocalStorage || []), ...order])];

  if (orderFromLocalStorage.length) {
    updateLocalStorageGridOptions(order, gridOptions, setGridOptions);
  }

  order.forEach((accessor) => {
    if (definedColumns[accessor]) cols.push(definedColumns[accessor]);
  });

  if (!gridOptions) {
    handleInitialGridOptions(cols, 250);
  } else {
    setColumns(cols);
  }
};

/**
 * Get price or contact us text
 */
export const getPriceOrContactUs = (
  price = 0,
  contactUsText = 'Contact us',
  interval = '',
  unit = '$',
) => {
  if (price === 0) return `${unit}${price}`;

  if (price > 0) {
    const validInterval = interval ? `/${interval}` : '';

    return `${unit}${price}${validInterval}`;
  }
  return contactUsText;
};

/**
 * Get color pattern based on plan name
 */
export const getColorByPlanName = (name: string) => {
  switch (name) {
    case 'Free':
      return 'lime';
    case 'Basic':
      return 'blue';
    case 'Growth':
    case 'Pro':
      return 'red';
    case 'Enterprise':
      return 'orange';
    default:
      return 'orange';
  }
};

declare global {
  interface Window {
    dataLayer: any;
  }
}

/**
 * Reset GTM dataLayer
 */
export const resetGtmDataLayer = () => {
  window.dataLayer.push(function (this: { reset: () => void }) {
    this.reset();
  });
};

/**
 * Send event to GTM and remove event data from GTM dataLayer
 */
export const sendEventAndClear = (data: any, baseUserEventData: any) => {
  TagManager.dataLayer({
    dataLayer: data,
  });

  resetGtmDataLayer();

  if (baseUserEventData) {
    TagManager.dataLayer({
      dataLayer: baseUserEventData,
    });
  }
};

/**
 * Deep freeze the values of an object so that they cannot be changed. Changes will throw an error.
 */
export const deepFreeze = (object: Record<string, any>) => {
  Object.values(object).forEach((value) => {
    if ((value && typeof value === 'object') || typeof value === 'function') {
      deepFreeze(value);
    }
  });

  return Object.freeze(object);
};

/**
 * Get URL to Stripe dashboard
 */
export const getStripeURL = (path: string = '/') => {
  const stripeUrl = getEnv('VITE_STRIPE_URL');
  if (!stripeUrl) return '';
  return `${removeTrailingSlash(stripeUrl)}/${path.replace(/^\/*/, '')}`;
};

/**
 * Generate tutorial path object
 */
export const generateTutorialIndexPath = (data: Record<string, any>) => {
  const res: Record<string, any> = {};

  for (const item in data) {
    const currentKey = data[item]?.page;
    if (currentKey && !res[currentKey]) {
      res[currentKey] = { index: 0 };
    }
  }
  return res;
};

/**
 * Check if user is ROLE_ADMIN
 */
export const isUserRoleAdmin = (user?: UserLocalStorage) => {
  if (!user) return false;
  return (
    (user.data?.roles || []).findIndex(
      (role) => 'ROLE_ADMIN'.indexOf(role) > -1,
    ) > -1 &&
    (getEnv('VITE_ADMIN_ORGANIZATIONS') || '').includes(user.data?.organization)
  );
};

export const getImportantResourceNames = () => {
  return [
    'cto_limit',
    'ctd_limit',
    'file_quota',
    'scoped_keys_limit',
    'api_calls_limit',
    'official_plugins_limit',
  ];
};

/**
 * Generate url to Flotiq main page
 */
export const getFlotiqMainPageUrl = (path: string) =>
  `${removeTrailingSlash(getEnv('VITE_FLOTIQ_PAGE_URL') || 'https://flotiq.com')}/${path.replace(/^\/*/, '')}`;

/**
 * Wait for element to be visible/invisible by selector to be in DOM
 */
export const getElementBySelector = async (
  selector: string,
  isVisible = true,
  interval = 200,
  timeout = 5000,
) => {
  let intervalId: NodeJS.Timeout;
  let timeoutId: NodeJS.Timeout;

  return Promise.race([
    new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        resolve(undefined);
      }, timeout);
    }),
    new Promise((resolve) => {
      const checkElement = () => {
        const element = document.querySelector(selector);
        if ((isVisible && element) || (!isVisible && !element)) {
          resolve(element);
        }
      };
      checkElement();
      intervalId = setInterval(checkElement, interval);
    }),
  ]).finally(() => {
    clearInterval(intervalId);
    clearTimeout(timeoutId);
  });
};

export const removeTrailingSlash = (url: string) =>
  url ? url.replace(/\/+$/, '') : '';

type Translatable<T> = {
  __translations?: Array<{ __language: string } & Partial<T>>;
};

export function getTranslatedField<
  T extends Translatable<T>,
  K extends keyof T = keyof T,
>(object: T, field: K, lang: string): T[K] {
  const translation = object?.__translations?.find(
    (translation) => translation?.__language == lang,
  );
  return (translation?.[field] as T[K]) || object?.[field];
}

export function getObjectDataFromDataUrl(dataUrl: string) {
  const split = dataUrl.split('/');
  return [split[split.length - 2], split[split.length - 1]];
}
